import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { AdvancedMarker, AdvancedMarkerAnchorPoint, InfoWindow, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import AdvancedMarkerWithRef from "./AdvancedMarkerWithRef";
import { AlertType, VehicleDriverChangeType, VehicleGridType } from "../../../../types/types";
import DriverChangeSummary from "./DriverChangeSummary";
import RouteLiveGuidance from "./RouteLiveGuidance";
import { routeApi } from "../../../../resources/Api/routeApi";
import { eventHandlerMessage, getTimezone, getVehicleIgnitionStatus, vehicleIgnitionStatusColors } from "../../../../functions/functions";
import { Box, Typography, Paper, Divider, List, ListItem, IconButton, Fade, Switch, styled, SwitchProps, Tooltip } from "@mui/material";
import { FileDownloadOutlined, TimerOutlined, PersonOutlineOutlined, NotificationImportantOutlined, CloseOutlined } from "@mui/icons-material";
import dayjs from "dayjs";
import liveCarIcon from "../../../../assets/files/cars/png/live-green-car.png";
import findRecentEventsByVehicle from "../../../../resources/Api/event/findRecentEventsByVehicle";

const IOSSwitch = styled((props: SwitchProps) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: 0, margin: 2, transitionDuration: '300ms',
        '&.Mui-checked': {
            transform: 'translateX(16px)', color: '#fff',
            '& + .MuiSwitch-track': { backgroundColor: '#65C466', opacity: 1, border: 0 },
            '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.5 },
        },
        '&.Mui-focusVisible .MuiSwitch-thumb': { color: '#33cf4d', border: '6px solid #fff' },
        '&.Mui-disabled .MuiSwitch-thumb': { color: theme.palette.grey[100] },
        '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.7 },
    },
    '& .MuiSwitch-thumb': { boxSizing: 'border-box', width: 22, height: 22 },
    '& .MuiSwitch-track': {
        borderRadius: 26 / 2, backgroundColor: '#E9E9EA', opacity: 1,
        transition: theme.transitions.create(['background-color'], { duration: 500 }),
    },
}));

type LatLng = { lat: number; lng: number; speed?: number };
type TelemetryAttributes = { ignition?: boolean; motion?: boolean };
type LiveTelemetry = LatLng & TelemetryAttributes & { speed: number; course: number; fixTimeMs: number };
type LiveEvent = {
    id: string;
    message: string;
    time: string;
    speed: number | null;
    ignition: string;
    motion: string;
    driver: string;
    isAlert?: boolean;
};
type OutsideRoutePoint = {
    id: string;
    lat: number;
    lng: number;
    time: string;
    speed: number;
    address: string | null;
    driver: string;
    deviceId: number;
    routeName: string | null;
    corridorMeters: number | null;
};

type Props = {
    vehicle: VehicleGridType;
    positions: VehicleGridType[];
    driverChanges: VehicleDriverChangeType[];
    driverHistoryLoading?: boolean;
    driverHistoryError?: boolean;
    animateEntry?: boolean;
    setTracking: (tracking: boolean) => void;
    routeToken: string;
    routeCustomerId: number;
    // Incrementa quando o usuário pede de novo para seguir o veículo (clique na grade)
    followRequest?: number;
};

const MAX_TRAIL_POINTS = 120;
const DISTANCE_LIMIT_METERS = 80;
const PREDICTION_TIMEOUT_MS = 90000;
const DAMPING_FACTOR = 0.35; // Equilíbrio ideal entre fluidez e precisão conforme solicitado

const distanceMetres = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371000;
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lng2 - lng1) * Math.PI) / 180;
    const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const GVehicleLiveTracker = ({ vehicle, positions, driverChanges, driverHistoryLoading, driverHistoryError, animateEntry = true, setTracking, routeToken, routeCustomerId, followRequest = 0 }: Props) => {
    const isMountedRef = useRef<boolean>(true);
    const map = useMap();
    const geometry = useMapsLibrary("geometry");
    const activeRouteId = vehicle.routeGuidance?.routeId ?? null;
    const activeShiftEndUtc = vehicle.routeGuidance?.shiftEndUtc ?? null;

    const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
    const polylineSegmentsRef = useRef<google.maps.Polyline[]>([]);
    const animationRef = useRef<number>();
    const lastFixTimeRef = useRef<number | null>(null);
    const lastAddressRef = useRef<string | null>(null);
    const lastStatusRef = useRef<string | null>(null);
    const lastAlertRef = useRef<string>("");
    const directionsServiceRef = useRef<google.maps.DirectionsService | null>(null);
    const directionsRequestRef = useRef(0);
    const initialPositionRef = useRef({ lat: vehicle.position.lat, lng: vehicle.position.lng });
    const initialCourseRef = useRef(vehicle.position?.course ?? 0);
    const displayedCourseRef = useRef(initialCourseRef.current);
    const autoZoomRef = useRef<number | null>(null);
    const lastCameraHeadingUpdateRef = useRef(0);
    const routeRequestRef = useRef(0);
    const plannedRouteVisibleRef = useRef(false);
    const selectedOutsidePointRef = useRef<string | null>(null);
    const outsidePointIdsRef = useRef(new Set<string>());

    const [path, setPath] = useState<LatLng[]>([]);
    const pathRef = useRef<LatLng[]>([]);
    const lastProcessedTimeRef = useRef<number>(0);
    const lastManualInteractionRef = useRef<number>(0);
    // Depois que o usuário arrasta ou dá zoom no mapa, o veículo deixa de ser seguido até ele pedir de novo
    const userMovedMapRef = useRef<boolean>(false);
    const lastTelemetryRef = useRef<LiveTelemetry | null>(null);
    const lastPathUpdateRef = useRef<number>(0);
    const isProjectingRef = useRef<boolean>(false);
    const [currentSpeed, setCurrentSpeed] = useState<number>(0);
    const [showPolyline, setShowPolyline] = useState<boolean>(true);
    const [plannedRoute, setPlannedRoute] = useState<{ routeId: number; path: google.maps.LatLng[] } | null>(null);
    const [routeLoading, setRouteLoading] = useState(false);
    const [routeError, setRouteError] = useState<string | null>(null);
    const [outsidePoints, setOutsidePoints] = useState<OutsideRoutePoint[]>([]);
    const [selectedOutsidePointId, setSelectedOutsidePointId] = useState<string | null>(null);
    selectedOutsidePointRef.current = selectedOutsidePointId;
    const selectedOutsidePoint = outsidePoints.find(point => point.id === selectedOutsidePointId);
    const visiblePlannedRoute = plannedRoute?.routeId === activeRouteId ? plannedRoute : null;
    plannedRouteVisibleRef.current = visiblePlannedRoute !== null;

    const getSpeedColor = useCallback((speedVal: number) => {
        if (speedVal > 80) return "#D50000";
        if (speedVal > 50) return "#FF6D00";
        if (speedVal > 0) return "#00C853";
        return "#555";
    }, []);

    const speedColor = getSpeedColor(currentSpeed);
    const [eventHistory, setEventHistory] = useState<LiveEvent[]>([]);
    const [addressNotification, setAddressNotification] = useState<{ text: string, show: boolean }>({ text: "", show: false });
    const [statusNotification, setStatusNotification] = useState<{ text: string, show: boolean }>({ text: "", show: false });
    const [alertNotification, setAlertNotification] = useState<{ text: string, show: boolean }>({ text: "", show: false });
    const [isMounted, setIsMounted] = useState(false);
    const [markerReady, setMarkerReady] = useState(false);
    const [vectorMapAvailable, setVectorMapAvailable] = useState(false);

    useEffect(() => {
        if (!map) return;
        const updateRenderingType = () => setVectorMapAvailable(
            map.getRenderingType() === google.maps.RenderingType.VECTOR
        );
        updateRenderingType();
        const listener = map.addListener("renderingtype_changed", updateRenderingType);
        return () => listener.remove();
    }, [map]);

    useEffect(() => {
        if (!map || !markerReady) return;
        const alignCarWithMap = () => {
            const content = markerRef.current?.content;
            const group = content instanceof HTMLElement
                ? content.querySelector<HTMLElement>(".directional-group") : null;
            if (group) group.style.transform = `rotate(${displayedCourseRef.current - (map.getHeading() ?? 0)}deg)`;
        };
        alignCarWithMap();
        const listener = map.addListener("heading_changed", alignCarWithMap);
        return () => listener.remove();
    }, [map, markerReady]);

    const getMarkerPosition = useCallback((fallback: LatLng): LatLng => {
        const position = markerRef.current?.position;
        const lat = typeof position?.lat === "function" ? position.lat() : position?.lat;
        const lng = typeof position?.lng === "function" ? position.lng() : position?.lng;
        return typeof lat === "number" && typeof lng === "number" && Number.isFinite(lat) && Number.isFinite(lng)
            ? { lat, lng, speed: fallback.speed } : fallback;
    }, []);

    const animateAlongRoute = useCallback((routePoints: LatLng[], totalDuration: number, targetCourse: number, drawTrail = true) => {
        if (!isMountedRef.current || !markerRef.current || !map || routePoints.length < 2 ||
            !Number.isFinite(totalDuration) || totalDuration <= 0 ||
            routePoints.some(point => !Number.isFinite(point.lat) || !Number.isFinite(point.lng))) return;

        if (animationRef.current) cancelAnimationFrame(animationRef.current);
        isProjectingRef.current = false;

        const lastTrailPoint = pathRef.current[pathRef.current.length - 1];
        const basePath = lastTrailPoint?.lat === routePoints[0].lat && lastTrailPoint?.lng === routePoints[0].lng
            ? pathRef.current : [...pathRef.current, routePoints[0]];
        const updateTrail = (point: LatLng, segmentIndex: number) => {
            // Guarda somente o trecho já percorrido, nunca os pontos futuros do Directions.
            pathRef.current = drawTrail
                ? [...basePath, ...routePoints.slice(Math.max(1, segmentIndex - MAX_TRAIL_POINTS), segmentIndex + 1), point].slice(-MAX_TRAIL_POINTS)
                : [point];
        };

        let elapsed = 0;
        let lastFrameTime: number | null = null;

        const step = (timestamp: number) => {
            if (!isMountedRef.current || !markerRef.current) return;
            // Não acumula deslocamento quando a aba fica suspensa ou perde frames.
            const deltaTime = lastFrameTime === null ? 0 : Math.min(Math.max(timestamp - lastFrameTime, 0), 100);
            lastFrameTime = timestamp;
            if (document.hidden) {
                animationRef.current = requestAnimationFrame(step);
                return;
            }

            elapsed += deltaTime;
            const progress = Math.min(elapsed / totalDuration, 1);

            let lat: number, lng: number;
            let roadAngle = targetCourse;

            if (progress < 1) {
                const totalSegments = routePoints.length - 1;
                const segmentIndex = Math.min(Math.floor(progress * totalSegments), totalSegments - 1);
                const segmentProgress = (progress * totalSegments) - segmentIndex;

                const start = routePoints[segmentIndex];
                const end = routePoints[segmentIndex + 1];

                lat = start.lat + (end.lat - start.lat) * segmentProgress;
                lng = start.lng + (end.lng - start.lng) * segmentProgress;
                if (end.lat !== start.lat || end.lng !== start.lng) {
                    roadAngle = Math.atan2(
                        (end.lng - start.lng) * Math.cos(lat * Math.PI / 180),
                        end.lat - start.lat
                    ) * 180 / Math.PI;
                }
                updateTrail({ lat, lng, speed: end.speed }, segmentIndex);
                if (drawTrail && timestamp - lastPathUpdateRef.current > 1000) {
                    lastPathUpdateRef.current = timestamp;
                    setPath(pathRef.current);
                }
            } else {
                // --- MODO PREDITIVO (DEAD RECKONING) ---
                const tel = lastTelemetryRef.current;
                const timeSinceLastUpdate = Date.now() - (tel?.fixTimeMs ?? NaN);
                if (!isProjectingRef.current) {
                    const target = routePoints[routePoints.length - 1];
                    markerRef.current.position = target;
                    updateTrail(target, routePoints.length - 2);
                    setPath(pathRef.current);
                }

                // Para a projeção sem movimento, com velocidade baixa ou posição com mais de 90 segundos.
                if (!tel?.ignition || tel.motion === false || !Number.isFinite(tel.speed) || tel.speed <= 1 ||
                    !Number.isFinite(timeSinceLastUpdate) || timeSinceLastUpdate < -5000 ||
                    timeSinceLastUpdate > PREDICTION_TIMEOUT_MS) {
                    return;
                }

                isProjectingRef.current = true;
                const { lat: sLat, lng: sLng } = getMarkerPosition(routePoints[routePoints.length - 1]);

                // Direção para projeção:
                // - Rotas com 3+ pontos (Google Directions): usa tangente do último segmento da via
                // - Rotas com 2 pontos (slide-in inicial): usa o curso GPS real do veículo (targetCourse)
                if (routePoints.length > 2) {
                    const lastIdx = routePoints.length - 1;
                    roadAngle = Math.atan2(
                        (routePoints[lastIdx].lng - routePoints[lastIdx - 1].lng) * Math.cos(sLat * Math.PI / 180),
                        routePoints[lastIdx].lat - routePoints[lastIdx - 1].lat
                    ) * 180 / Math.PI;
                } else {
                    roadAngle = targetCourse;
                }

                // Cálculo vetorial básico de deslocamento com amortecimento
                const speedMs = (tel.speed / 3.6) * DAMPING_FACTOR;
                const latStep = (speedMs * Math.cos((roadAngle * Math.PI) / 180)) / 111320;
                const lngStep = (speedMs * Math.sin((roadAngle * Math.PI) / 180)) / (111320 * Math.cos((sLat * Math.PI) / 180));

                lat = sLat + (latStep * deltaTime) / 1000;
                lng = sLng + (lngStep * deltaTime) / 1000;
                if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return;

                // Atualiza o rastro (linha) durante a projeção a cada 1 segundo
                if (timestamp - lastPathUpdateRef.current > 1000) {
                    lastPathUpdateRef.current = timestamp;
                    pathRef.current = [...pathRef.current, { lat, lng, speed: tel.speed }].slice(-MAX_TRAIL_POINTS);
                    setPath(pathRef.current);
                }
            }

            markerRef.current.position = { lat, lng };

            // Seguir o veículo se não houver interação manual recente (5 segundos)
            if (map && !plannedRouteVisibleRef.current && selectedOutsidePointRef.current === null &&
                (!userMovedMapRef.current && Date.now() - lastManualInteractionRef.current > 5000)) {
                map.setCenter({ lat, lng });
                if (map.getRenderingType() === google.maps.RenderingType.VECTOR &&
                    timestamp - lastCameraHeadingUpdateRef.current >= 100) {
                    lastCameraHeadingUpdateRef.current = timestamp;
                    map.setHeading((roadAngle + 360) % 360);
                }
            }

            displayedCourseRef.current = roadAngle;
            const content = markerRef.current?.content;
            if (content instanceof HTMLElement) {
                const directionalGroup = content.querySelector<HTMLElement>('.directional-group');
                if (directionalGroup) {
                    directionalGroup.style.transform = `rotate(${roadAngle - (map.getHeading() ?? 0)}deg)`;
                }
            }

            // O loop agora é contínuo enquanto as regras de parada permitirem
            animationRef.current = requestAnimationFrame(step);
        };
        animationRef.current = requestAnimationFrame(step);
    }, [map, getMarkerPosition]);

    // A câmera inclinada pertence apenas ao acompanhamento ao vivo. Mapas raster
    // continuam em 2D, sem alterar a posição ou o fluxo de telemetria.
    useEffect(() => {
        if (!map || !vectorMapAvailable) return;
        const previousTilt = map.getTilt() ?? 0;
        const previousHeading = map.getHeading() ?? 0;
        const course = initialCourseRef.current;
        map.setTilt(45);
        if (typeof course === "number" && Number.isFinite(course)) {
            map.setHeading((course + 360) % 360);
        }
        return () => {
            map.setTilt(previousTilt);
            map.setHeading(previousHeading);
        };
    }, [map, vehicle.id, vectorMapAvailable]);

    /* ---------------- INITIALIZATION ---------------- */

    useEffect(() => {
        const routeRequestVersion = routeRequestRef;
        setIsMounted(true);
        isMountedRef.current = true;
        directionsRequestRef.current++;
        if (!directionsServiceRef.current && window.google && typeof google.maps.DirectionsService === "function") {
            directionsServiceRef.current = new google.maps.DirectionsService();
        }

        return () => {
            setIsMounted(false);
            isMountedRef.current = false;
            routeRequestVersion.current++;
            if (polylineSegmentsRef.current) {
                polylineSegmentsRef.current.forEach(l => l.setMap(null));
            }
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
        };
    }, []);

    useEffect(() => {
        routeRequestRef.current++;
        setPlannedRoute(null);
        setRouteLoading(false);
        setRouteError(null);
    }, [activeRouteId, activeShiftEndUtc, vehicle.id]);

    const togglePlannedRoute = useCallback(async () => {
        if (visiblePlannedRoute) {
            setPlannedRoute(null);
            const markerPosition = markerRef.current?.position;
            if (markerPosition && map) {
                map.panTo(markerPosition);
                autoZoomRef.current = 17;
                map.setZoom(17);
                if (map.getRenderingType() === google.maps.RenderingType.VECTOR) {
                    map.setTilt(45);
                    const course = lastTelemetryRef.current?.course;
                    if (course !== undefined) map.setHeading((course + 360) % 360);
                }
            }
            return;
        }
        if (activeRouteId === null || !geometry || !routeToken || routeCustomerId <= 0) return;
        const request = ++routeRequestRef.current;
        setRouteLoading(true);
        setRouteError(null);
        try {
            const route = await routeApi.find(routeToken, routeCustomerId, activeRouteId);
            if (!isMountedRef.current || request !== routeRequestRef.current) return;
            if (route.id !== activeRouteId) throw new Error("Rota diferente da programação atual.");
            const path = geometry.encoding.decodePath(route.encodedPolyline);
            if (path.length < 2) throw new Error("Traçado da rota inválido.");
            setPlannedRoute({ routeId: activeRouteId, path });
        } catch {
            if (isMountedRef.current && request === routeRequestRef.current) {
                setRouteError("Não foi possível visualizar a rota prevista. Tente novamente.");
            }
        } finally {
            if (isMountedRef.current && request === routeRequestRef.current) setRouteLoading(false);
        }
    }, [activeRouteId, geometry, map, routeToken, routeCustomerId, visiblePlannedRoute]);

    useEffect(() => {
        if (!map || !visiblePlannedRoute) return;
        const line = new google.maps.Polyline({
            map, path: visiblePlannedRoute.path, strokeColor: "#2563eb",
            strokeOpacity: 0.9, strokeWeight: 5, zIndex: 1,
        });
        const bounds = new google.maps.LatLngBounds();
        visiblePlannedRoute.path.forEach(point => bounds.extend(point));
        map.fitBounds(bounds, { top: 24, bottom: 24, left: 24, right: window.innerWidth >= 640 ? 390 : 24 });
        return () => line.setMap(null);
    }, [map, visiblePlannedRoute]);

    const lastVehicleIdRef = useRef<number | null>(null);

    /**
     * Extrai attributes (ignition, motion, etc.) de forma segura.
     * Prioridade: vehicle.attributes (raiz, já parseado pela API) > vehicle.position.attributes (pode ser string JSON)
     */
    const safeGetAttributes = useCallback((vehicleObj: VehicleGridType & { attributes?: TelemetryAttributes }): TelemetryAttributes => {
        let attrs: unknown = vehicleObj.attributes?.ignition !== undefined
            ? vehicleObj.attributes : vehicleObj.position?.attributes;
        if (typeof attrs === "string") {
            try { attrs = JSON.parse(attrs); } catch { return {}; }
        }
        if (!attrs || typeof attrs !== "object" || Array.isArray(attrs)) return {};
        const values = attrs as Record<string, unknown>;
        return {
            ignition: typeof values.ignition === "boolean" ? values.ignition : undefined,
            motion: typeof values.motion === "boolean" ? values.motion : undefined,
        };
    }, []);

    // Popula o histórico de eventos inicial APENAS uma vez quando o veículo é selecionado
    useEffect(() => {
        if (!vehicle.id || !vehicle.position) return;

        // Se já inicializamos este veículo, não fazemos nada (o loop principal cuida do resto)
        if (lastVehicleIdRef.current === vehicle.id) return;
        lastVehicleIdRef.current = vehicle.id;

        const pos = vehicle.position;
        const tel = safeGetAttributes(vehicle);
        const positionTime = pos.zonedFixTime ? dayjs(pos.zonedFixTime) : dayjs.utc(pos.fixTime ?? NaN);
        const localTime = positionTime.isValid() ? positionTime.tz(getTimezone(vehicle.utcZone)).format("HH:mm:ss") : "--:--:--";

        const initialEvent = {
            id: `initial-${Date.now()}`,
            message: pos.address || "Posição atual",
            time: localTime,
            speed: Math.round(pos.speed || 0),
            ignition: tel.ignition ? "Ligada" : "Desligada",
            motion: tel.motion ? "Sim" : "Não",
            driver: vehicle.driver?.name || vehicle.driverUnrecorded || "N/A"
        };

        setEventHistory([initialEvent]);
        lastFixTimeRef.current = positionTime.valueOf();
    }, [vehicle, safeGetAttributes]);

    // Apenas o veículo acompanhado; não acrescenta trabalho à consulta de posição de 10 s.
    useEffect(() => {
        if (!routeToken || routeCustomerId <= 0 || !vehicle.id) return;
        let active = true;
        let pending = false;
        let cursor: number | null = null;
        let lastRequestAt = 0;

        const poll = async () => {
            if (!active || pending || document.hidden || Date.now() - lastRequestAt < 30000) return;
            pending = true;
            lastRequestAt = Date.now();
            try {
                if (cursor === null) {
                    const baseline = await findRecentEventsByVehicle(routeToken, routeCustomerId, vehicle.id, -1);
                    if (active) cursor = baseline.cursor;
                    return;
                }
                const received = [] as Awaited<ReturnType<typeof findRecentEventsByVehicle>>["events"];
                for (let page = 0; page < 2 && active; page++) {
                    const result = await findRecentEventsByVehicle(routeToken, routeCustomerId, vehicle.id, cursor);
                    if (!active) return;
                    if (result.events.length) {
                        cursor = Math.max(cursor, ...result.events.map(event => event.id));
                        received.push(...result.events);
                    }
                    if (!result.hasMore) {
                        cursor = Math.max(cursor, result.cursor);
                        break;
                    }
                }
                if (!received.length) return;
                const newEvents = received.map(event => ({
                    id: `server-${event.id}`,
                    message: `ALERTA: ${eventHandlerMessage(event.name)}`,
                    time: event.eventDateTime.match(/\d{2}:\d{2}:\d{2}/)?.[0] ?? event.eventDateTime,
                    speed: null,
                    ignition: "-",
                    motion: "-",
                    driver: event.vehicle.driverName || "N/A",
                    isAlert: true,
                }));
                setEventHistory(previous => {
                    const known = new Set(previous.map(event => event.id));
                    const unique = newEvents.filter(event => !known.has(event.id) &&
                        !previous.some(current => current.message === event.message && current.time === event.time));
                    return unique.length ? [...unique.reverse(), ...previous].slice(0, 100) : previous;
                });
            } catch {
                // A telemetria continua normalmente se a consulta auxiliar falhar.
            } finally {
                pending = false;
            }
        };

        void poll();
        const intervalId = window.setInterval(() => { void poll(); }, 30000);
        const onVisible = () => { if (!document.hidden) void poll(); };
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            active = false;
            window.clearInterval(intervalId);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [routeToken, routeCustomerId, vehicle.id]);

    /* ---------------- ANIMATION LOGIC (FOLLOW ROADS) ---------------- */

    useEffect(() => {
        if (!map) return;
        const onInteraction = () => { lastManualInteractionRef.current = Date.now(); };
        const onUserMovedMap = () => {
            userMovedMapRef.current = true;
            onInteraction();
        };

        const l1 = map.addListener('dragstart', onUserMovedMap);
        const l2 = map.addListener('zoom_changed', () => {
            if (map.getZoom() !== autoZoomRef.current) onUserMovedMap();
        });
        const l3 = map.addListener('click', onInteraction);

        return () => {
            google.maps.event.removeListener(l1);
            google.maps.event.removeListener(l2);
            google.maps.event.removeListener(l3);
        };
    }, [map]);

    // Pedido explícito para voltar a seguir o veículo: libera o acompanhamento e centraliza na hora
    useEffect(() => {
        if (followRequest === 0) return;
        userMovedMapRef.current = false;
        lastManualInteractionRef.current = 0;

        const markerPosition = markerRef.current?.position;
        if (map && markerPosition && !plannedRouteVisibleRef.current && selectedOutsidePointRef.current === null) {
            map.panTo(markerPosition);
        }
    }, [followRequest, map]);

    /* ---------------- DATA PROCESSING ---------------- */

    useEffect(() => {
        if (!map || !window.google || !markerReady || !markerRef.current) return;
        if (!directionsServiceRef.current && typeof google.maps.DirectionsService === "function") {
            directionsServiceRef.current = new google.maps.DirectionsService();
        }

        const updatedPos = positions.find(p => p.id === vehicle.id);
        if (!updatedPos || !updatedPos.position) return;

        const { lat, lng, speed, address, fixTime, zonedFixTime } = updatedPos.position;
        const course = updatedPos.position.course ?? 0;
        if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180 ||
            !Number.isFinite(speed) || !Number.isFinite(course)) return;

        // Use zonedFixTime se disponível para exibir a hora correta conforme o timezone do cliente
        const positionTime = zonedFixTime ? dayjs(zonedFixTime) : dayjs.utc(fixTime ?? NaN);
        const fixTimeMs = positionTime.valueOf();
        if (!Number.isFinite(fixTimeMs) || fixTimeMs < lastProcessedTimeRef.current) return;
        displayedCourseRef.current = course;

        // Extrai ignition/motion via helper centralizado (raiz > position.attributes)
        const attrs = safeGetAttributes(updatedPos);
        const ignition = attrs.ignition;
        const motion = attrs.motion;

        // Salva telemetria para o motor de predição (inclui motion)
        lastTelemetryRef.current = { lat, lng, speed, course, ignition, motion, fixTimeMs };
        if (!plannedRouteVisibleRef.current && selectedOutsidePointRef.current === null &&
            !userMovedMapRef.current && Date.now() - lastManualInteractionRef.current > 5000 &&
            map.getRenderingType() === google.maps.RenderingType.VECTOR) {
            map.setHeading((course + 360) % 360);
        }
        const markerContent = markerRef.current.content;
        const directionalGroup = markerContent instanceof HTMLElement
            ? markerContent.querySelector<HTMLElement>('.directional-group') : null;
        if (directionalGroup) {
            directionalGroup.style.transform = `rotate(${course - (map.getHeading() ?? 0)}deg)`;
        }

        const driverName = updatedPos.driver?.name || updatedPos.driverUnrecorded || "N/A";
        const localTime = positionTime.tz(getTimezone(updatedPos.utcZone)).format("HH:mm:ss");
        const guidance = updatedPos.routeGuidance;
        const positionFixUtc = dayjs.utc(fixTime ?? NaN).valueOf();
        const evaluatedFixUtc = dayjs.utc(guidance?.lastPositionTimeUtc ?? NaN).valueOf();
        if (guidance?.routeId != null && guidance.outsideSequence > 0 &&
            (guidance.status === "VERIFYING" || guidance.status === "POSSIBLE_DEVIATION") &&
            Number.isFinite(positionFixUtc) && positionFixUtc === evaluatedFixUtc) {
            const pointId = updatedPos.position.id > 0
                ? String(updatedPos.position.id)
                : `${fixTime}-${lat}-${lng}`;
            if (!outsidePointIdsRef.current.has(pointId)) {
                outsidePointIdsRef.current.add(pointId);
                setOutsidePoints(previous => [...previous, {
                    id: pointId, lat, lng,
                    time: positionTime.tz(getTimezone(updatedPos.utcZone)).format("DD/MM/YYYY HH:mm:ss"),
                    speed, address: address || null, driver: driverName,
                    deviceId: updatedPos.position.deviceId,
                    routeName: guidance.routeName, corridorMeters: guidance.corridorMeters,
                }]);
            }
        }

        const hasAlerts = updatedPos.hasAlerts;
        const alerts = updatedPos.alerts || [];
        const alertMessages = alerts.map((a: AlertType) => a.description).join(", ");

        /* ---------------- EVENTS ---------------- */

        if (fixTimeMs > (lastFixTimeRef.current || 0)) {
            lastFixTimeRef.current = fixTimeMs;
            setEventHistory(prev => {
                if (prev.length > 0 && prev[0].id === `pos-${fixTimeMs}`) return prev;
                const newEvents: LiveEvent[] = [];

                // Se houver alerta, adiciona como evento prioritário
                if (hasAlerts && alerts.length > 0) {
                    alerts.forEach((alert: AlertType, idx: number) => {
                        newEvents.push({
                            id: `alert-${fixTimeMs}-${idx}`,
                            message: `ALERTA: ${alert.description}`,
                            time: localTime,
                            speed: Math.round(speed || 0),
                            ignition: ignition ? "Ligada" : "Desligada",
                            motion: motion ? "Sim" : "Não",
                            driver: driverName,
                            isAlert: true
                        });
                    });
                }

                newEvents.push({
                    id: `pos-${fixTimeMs}`, message: address || "Atualização de posição", time: localTime,
                    speed: Math.round(speed || 0), ignition: ignition ? "Ligada" : "Desligada",
                    motion: motion ? "Sim" : "Não", driver: driverName
                });

                return [...newEvents, ...prev].slice(0, 100);
            });
        }

        if (address && address !== lastAddressRef.current && address !== "Endereço não disponível") {
            lastAddressRef.current = address;
            setAddressNotification({ text: address, show: true });
            setTimeout(() => { if (isMountedRef.current) setAddressNotification(prev => ({ ...prev, show: false })); }, 4000);
        }

        const isIdle = ignition === true && motion === false && updatedPos.idle !== false;
        const statusKey = `${ignition}-${motion}-${isIdle}`;
        if (isIdle && lastStatusRef.current !== statusKey) {
            lastStatusRef.current = statusKey;
            setStatusNotification({ text: getVehicleIgnitionStatus(attrs, vehicle.position?.protocol, updatedPos.idle).label, show: true });
            setTimeout(() => { if (isMountedRef.current) setStatusNotification(prev => ({ ...prev, show: false })); }, 4000);
        } else {
            lastStatusRef.current = statusKey;
        }

        if (hasAlerts && alertMessages && lastAlertRef.current !== alertMessages) {
            lastAlertRef.current = alertMessages;
            setAlertNotification({ text: alertMessages, show: true });
            setTimeout(() => { if (isMountedRef.current) setAlertNotification(prev => ({ ...prev, show: false })); }, 5000);
        }

        /* ---------------- ROUTE & ANIMATION ---------------- */

        // Proteção contra re-processamento da mesma posição
        if (fixTimeMs <= lastProcessedTimeRef.current && pathRef.current.length > 0) return;
        lastProcessedTimeRef.current = fixTimeMs;
        const requestVersion = ++directionsRequestRef.current;

        setCurrentSpeed(Math.round(speed || 0));

        const processUpdate = () => {
            const target = { lat, lng, speed };

            if (pathRef.current.length === 0) {
                // ---- PRIMEIRO POSICIONAMENTO (INICIAL) ----

                if (!markerRef.current) return;

                // Verifica condições para decidir se inicia em movimento ou estático
                // Usa zonedFixTime (hora local) para comparar com Date.now(), pois fixTime vem em UTC sem sufixo Z
                const now = Date.now();
                const ageMs = now - fixTimeMs;
                const isRecent = ageMs >= -5000 && ageMs < 120000; // < 2 minutos
                const isMoving = speed > 1;
                const isMotion = motion === true;

                /*console.log('[LiveTracker] initialPosition →', {
                    speed, motion, ignition, isRecent, isMoving, isMotion, ageMs,
                    zonedFixTime, fixTime
                });*/

                if (!plannedRouteVisibleRef.current && selectedOutsidePointRef.current === null) {
                    autoZoomRef.current = 17;
                    map.setZoom(17);
                }

                if (animateEntry && isRecent && isMoving && isMotion) {
                    // Veículo em movimento recente: faz slide-in + projeção de movimento
                    const mapCenter = map.getCenter();
                    let startPoint = mapCenter
                        ? { lat: mapCenter.lat(), lng: mapCenter.lng(), speed: 0 }
                        : { ...target, speed: 0 };

                    const entryDistance = distanceMetres(startPoint.lat, startPoint.lng, target.lat, target.lng);
                    if (entryDistance < 1 || entryDistance > DISTANCE_LIMIT_METERS) {
                        startPoint = { lat: target.lat - 0.0005, lng: target.lng - 0.0005, speed: 0 };
                    }

                    setPath([]);
                    pathRef.current = [startPoint];
                    animateAlongRoute([startPoint, target], 3000, course || 0, false);
                } else {
                    // Na troca de veículo, posição antiga ou veículo parado, não simula deslocamento de entrada.
                    markerRef.current.position = target;
                    if (!plannedRouteVisibleRef.current && selectedOutsidePointRef.current === null) map.setCenter(target);

                    setPath([target]);
                    pathRef.current = [target];
                }
                return;
            }

            const lastPoint = getMarkerPosition(pathRef.current[pathRef.current.length - 1]);
            // A nova posição substitui a animação pendente, inclusive enquanto o Directions responde.
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
            animationRef.current = undefined;
            setPath(pathRef.current);
            if (lastPoint.lat === lat && lastPoint.lng === lng) return;

            // ZOOM INTELIGENTE baseado na velocidade
            let zoomLevel = 17;
            if (speed > 80) zoomLevel = 15;
            else if (speed > 50) zoomLevel = 16;
            if (!plannedRouteVisibleRef.current && selectedOutsidePointRef.current === null &&
                !userMovedMapRef.current && Date.now() - lastManualInteractionRef.current > 5000) {
                autoZoomRef.current = zoomLevel;
                map.setZoom(zoomLevel);
            }

            const dist = distanceMetres(lastPoint.lat, lastPoint.lng, target.lat, target.lng);

            // Calcula duração baseada na velocidade REAL (m/s) para maior fidelidade
            // Se o veículo estiver lento demais ou parado, usa 30s como base de animação
            let duration = (speed > 5) ? (dist / (speed / 3.6)) * 1000 : 30000;

            // Cap para evitar animações instantâneas ou extremamente longas
            duration = Math.min(Math.max(duration, 5000), 45000);

            if (dist < DISTANCE_LIMIT_METERS || !directionsServiceRef.current) {
                const routePoints = [{ lat: lastPoint.lat, lng: lastPoint.lng, speed: lastPoint.speed || speed }, target];
                animateAlongRoute(routePoints, duration, course || 0);
            } else if (directionsServiceRef.current) {
                directionsServiceRef.current.route({
                    origin: lastPoint,
                    destination: target,
                    travelMode: google.maps.TravelMode.DRIVING
                }, (result, status) => {
                    if (!isMountedRef.current || requestVersion !== directionsRequestRef.current) return;

                    const origin = getMarkerPosition(lastPoint);
                    let routePoints = [origin, target];
                    if (status === google.maps.DirectionsStatus.OK && result?.routes[0]?.overview_path) {
                        const overview = result.routes[0].overview_path.map(p => ({ lat: p.lat(), lng: p.lng(), speed }));
                        if (overview.length > 1 && overview.every(p => Number.isFinite(p.lat) && Number.isFinite(p.lng))) {
                            overview[0] = { ...origin, speed: origin.speed ?? speed };
                            overview[overview.length - 1] = target;
                            routePoints = overview;
                        }
                    }

                    animateAlongRoute(routePoints, duration, course || 0);
                });
            }
        };

        processUpdate();

    }, [positions, map, vehicle, animateEntry, animateAlongRoute, getMarkerPosition, isMounted, markerReady, safeGetAttributes]);

    /* ---------------- POLYLINE RENDERING ---------------- */

    useEffect(() => {
        if (!map || path.length < 2 || !showPolyline) {
            polylineSegmentsRef.current.forEach(l => l.setMap(null));
            return;
        }

        // Limpa polilinhas anteriores
        polylineSegmentsRef.current.forEach(l => l.setMap(null));
        polylineSegmentsRef.current = [];

        let currentSegments: LatLng[] = [];
        let currentColor: string | null = null;

        for (let i = 0; i < path.length; i++) {
            const pt = path[i];
            const color = getSpeedColor(pt.speed || 0);

            if (currentColor === null) {
                currentColor = color;
                currentSegments.push(pt);
            } else if (color === currentColor) {
                currentSegments.push(pt);
            } else {
                const poly = new google.maps.Polyline({
                    path: currentSegments,
                    strokeColor: currentColor,
                    strokeOpacity: 1,
                    strokeWeight: 4,
                    map: map,
                    zIndex: 2
                });
                polylineSegmentsRef.current.push(poly);

                const lastPt = currentSegments[currentSegments.length - 1];
                currentSegments = [lastPt, pt];
                currentColor = color;
            }
        }

        if (currentSegments.length > 1 && currentColor) {
            const poly = new google.maps.Polyline({
                path: currentSegments,
                strokeColor: currentColor,
                strokeOpacity: 1,
                strokeWeight: 4,
                map: map,
                zIndex: 2
            });
            polylineSegmentsRef.current.push(poly);
        }
    }, [path, map, getSpeedColor, showPolyline]);

    /* ---------------- HELPERS ---------------- */

    const handleExport = () => {
        const headers = ["Hora", "Evento", "Velocidade", "Ignição", "Movimento", "Motorista"];
        const rows = eventHistory.map(e => [e.time, e.message, e.speed ?? "-", e.ignition, e.motion, e.driver]);
        const csv = ["\ufeff" + headers.join(";"), ...rows.map(r => r.join(";"))].join("\n");
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `Eventos_${vehicle.vehiclePlate}.csv`;
        link.click();
    };

    /* ---------------- RENDER ---------------- */

    return (
        <>
        <AdvancedMarkerWithRef
            getRef={(ref) => {
                markerRef.current = ref;
                if (ref && !markerReady) setMarkerReady(true);
            }}
            position={initialPositionRef.current}
            anchorPoint={AdvancedMarkerAnchorPoint.CENTER}
            clickable
            collisionBehavior="REQUIRED_AND_HIDES_OPTIONAL"
            onMarkerClick={() => setTracking(false)}
        >
            <Box sx={{ width: 88, height: 88, position: "relative" }}>

                {/* NOTIFICATIONS */}
                <Box sx={{ position: 'absolute', top: -100, display: 'flex', flexDirection: 'column', alignItems: 'center', width: 300, pointerEvents: 'none', zIndex: 10 }}>
                    <Fade in={alertNotification.show}>
                        <Box sx={{ background: '#d32f2f', color: '#fff', p: '8px 16px', borderRadius: '12px', fontSize: '11px', textAlign: 'center', backdropFilter: 'blur(8px)', boxShadow: '0 4px 12px rgba(211, 47, 47, 0.4)', mb: '6px', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <NotificationImportantOutlined fontSize="small" /> {alertNotification.text}
                        </Box>
                    </Fade>
                    <Fade in={statusNotification.show}>
                        <Box sx={{ background: vehicleIgnitionStatusColors.idle, color: '#fff', p: '4px 12px', borderRadius: '16px', fontSize: '11px', fontWeight: 'bold', mb: '6px', boxShadow: '0 4px 12px rgba(237, 108, 2, 0.4)', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <NotificationImportantOutlined fontSize="small" /> {statusNotification.text}
                        </Box>
                    </Fade>
                    <Fade in={addressNotification.show}>
                        <Box sx={{ background: 'rgba(9, 65, 74, 0.95)', color: '#fff', p: '8px 16px', borderRadius: '12px', fontSize: '11px', textAlign: 'center', backdropFilter: 'blur(8px)', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
                            {addressNotification.text}
                        </Box>
                    </Fade>
                </Box>

                {/* DIRECTIONAL GROUP */}
                <Box className="directional-group" sx={{
                    position: "absolute", inset: 0, display: "flex", justifyContent: "center", alignItems: "center",
                    transform: `rotate(${(vehicle.position?.course ?? 0) - (map?.getHeading() ?? 0)}deg)`,
                }}>
                    <Box sx={{ position: "absolute", top: -11, width: 0, height: 0, borderLeft: "7px solid transparent", borderRight: "7px solid transparent", borderBottom: `14px solid ${speedColor}`, filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))" }} />
                    <img
                        alt="Veículo em acompanhamento ao vivo"
                        src={liveCarIcon}
                        width={76}
                        height={76}
                        draggable={false}
                        style={{ objectFit: "contain", transform: "rotate(-90deg)", filter: "drop-shadow(0 3px 4px rgba(0,0,0,0.35))" }}
                    />
                </Box>
                <Box sx={{ position: "absolute", top: 85, left: "50%", transform: "translateX(-50%)", background: speedColor, color: "#fff", fontSize: 11, fontWeight: "bold", padding: "3px 8px", borderRadius: "10px", zIndex: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.2)', whiteSpace: 'nowrap' }}>
                    {currentSpeed} km/h
                </Box>
            </Box>

            {/* SIDEBAR PORTAL */}
            {isMounted && createPortal(
                <Paper
                    elevation={8}
                    className="vehicle-live-card tw-flex tw-flex-col tw-overflow-hidden tw-border tw-border-slate-200/80 tw-bg-white/95 tw-font-prometo tw-shadow-xl tw-backdrop-blur-md tw-transition-all tw-duration-300 tw-ease-gtr-smooth dark:tw-border-slate-700 dark:tw-bg-slate-900/95"
                    sx={{
                        position: "fixed",
                        top: { xs: 64, sm: 67 },
                        right: { xs: 64, sm: 72 },
                        left: { xs: 8, sm: "auto" },
                        width: { xs: "auto", sm: 340 },
                        maxHeight: { xs: "calc(100vh - 136px)", sm: "min(68vh, 640px)" },
                        borderRadius: { xs: 2.5, sm: 3 },
                        zIndex: 9999,
                    }}
                >
                    <Box
                        className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-bg-gtr-sidebar tw-px-3.5 tw-py-3 tw-text-white"
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Box className="tw-flex tw-items-center tw-gap-2">
                                <TimerOutlined sx={{ fontSize: 19 }} />
                                <Typography sx={{ fontWeight: 800, fontSize: 13, color: "inherit", lineHeight: 1.2 }}>
                                    ACOMPANHAMENTO AO VIVO
                                </Typography>
                                <Box
                                    component="span"
                                    aria-hidden="true"
                                    className="tw-h-2 tw-w-2 tw-flex-none tw-animate-pulse tw-rounded-full tw-bg-gtr-green"
                                />
                                {vehicle.hasAlerts && (
                                    <Tooltip title="O veículo possui alertas" arrow placement="top">
                                        <NotificationImportantOutlined className="tw-animate-pulse" sx={{ color: "#ff5252", fontSize: 18 }} />
                                    </Tooltip>
                                )}
                            </Box>
                            <Typography noWrap sx={{ fontSize: 10.5, fontWeight: 700, color: "rgba(255,255,255,0.72)", mt: 0.45 }}>
                                VEÍCULO: <Box component="span" sx={{ color: "#fff" }}>{vehicle.vehiclePlate}</Box>
                            </Typography>
                        </Box>
                        <Box className="tw-flex tw-flex-none tw-items-center tw-gap-1.5">
                            <Tooltip title="Exportar eventos" arrow placement="top">
                                <IconButton
                                    aria-label="Exportar eventos do acompanhamento ao vivo"
                                    onClick={handleExport}
                                    size="small"
                                    className="!tw-bg-white/10 !tw-text-white tw-transition-all tw-duration-200 hover:!tw-bg-white/20"
                                >
                                    <FileDownloadOutlined fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Fechar acompanhamento" arrow placement="top">
                                <IconButton
                                    aria-label="Fechar acompanhamento ao vivo"
                                    onClick={() => setTracking(false)}
                                    size="small"
                                    className="!tw-bg-white/10 !tw-text-white tw-transition-all tw-duration-200 hover:!tw-bg-red-500/70"
                                >
                                    <CloseOutlined fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                    <Divider />
                    <Box className="vehicle-live-summary tw-flex tw-items-center tw-justify-between tw-gap-3 tw-bg-slate-50 tw-px-3.5 tw-py-2 dark:tw-bg-slate-950">
                        <Box>
                            <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: "#475569", lineHeight: 1.2 }}>
                                EXIBIR RASTRO NO MAPA
                            </Typography>
                            <Typography sx={{ fontSize: 9.5, color: "#94a3b8", mt: 0.25 }}>
                                {eventHistory.length} evento(s) nesta sessão
                            </Typography>
                        </Box>
                        <Tooltip title="Exibir ou ocultar o rastro percorrido" arrow placement="left">
                            <IOSSwitch
                                checked={showPolyline}
                                onChange={e => setShowPolyline(e.target.checked)}
                                inputProps={{ "aria-label": "Exibir ou ocultar o rastro percorrido" }}
                            />
                        </Tooltip>
                    </Box>
                    <Divider />
                    <DriverChangeSummary vehicle={vehicle} history={driverChanges} compact loading={driverHistoryLoading} error={driverHistoryError} />
                    <Divider />
                    <RouteLiveGuidance
                        guidance={vehicle.routeGuidance}
                        routeVisible={visiblePlannedRoute !== null}
                        routeLoading={routeLoading}
                        routeAvailable={Boolean(geometry && routeToken && routeCustomerId > 0)}
                        routeError={routeError}
                        onToggleRoute={togglePlannedRoute}
                    />
                    {vehicle.routeGuidance && <Divider />}
                    <List
                        aria-label="Linha do tempo do acompanhamento ao vivo"
                        sx={{
                            overflowY: "auto",
                            flex: 1,
                            p: 0,
                            scrollbarWidth: "thin",
                            scrollbarColor: "#94a3b8 transparent",
                            "&::-webkit-scrollbar": { width: 5 },
                            "&::-webkit-scrollbar-thumb": { backgroundColor: "#94a3b8", borderRadius: 8 },
                        }}
                    >
                        {eventHistory.length === 0 ? (
                            <Box className="vehicle-live-empty tw-flex tw-flex-col tw-items-center tw-justify-center tw-px-4 tw-py-8 tw-text-center">
                                <TimerOutlined sx={{ color: "#94a3b8", fontSize: 28 }} />
                                <Typography sx={{ mt: 1, fontSize: 11, fontWeight: 700, color: "#64748b" }}>
                                    Aguardando novos eventos do veículo
                                </Typography>
                            </Box>
                        ) : eventHistory.map((ev) => (
                            <ListItem
                                key={ev.id}
                                divider
                                className="vehicle-live-event tw-transition-colors tw-duration-200 hover:tw-bg-slate-50 dark:hover:tw-bg-slate-800"
                                sx={{ display: "block", px: 1.75, py: 1.15 }}
                            >
                                <Box className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            className="vehicle-live-event-message"
                                            sx={{
                                                fontSize: 11.5,
                                                color: ev.isAlert ? "#d32f2f" : "#263b40",
                                                fontWeight: ev.isAlert ? 800 : 600,
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {ev.isAlert && <NotificationImportantOutlined sx={{ fontSize: 15, flex: "none" }} />}
                                            {ev.message}
                                        </Typography>
                                        <Box className="vehicle-live-event-meta tw-mt-1 tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-0.5">
                                            <Typography component="span" sx={{ fontSize: 9.5, fontWeight: 800, color: "#006c77" }}>
                                                {ev.time}
                                            </Typography>
                                            <Box className="tw-flex tw-items-center tw-gap-1" sx={{ fontSize: 9.5, color: "#64748b", fontWeight: 600 }}>
                                                <PersonOutlineOutlined sx={{ fontSize: 13 }} /> {ev.driver}
                                            </Box>
                                            <Typography component="span" sx={{ fontSize: 9.5, color: "#64748b", fontWeight: 600 }}>
                                                Ign: {ev.ignition} · Movi: {ev.motion}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box
                                        className="tw-flex-none tw-rounded-md tw-px-2 tw-py-0.5"
                                        sx={{
                                            background: ev.speed !== null && ev.speed > 0 ? `${getSpeedColor(ev.speed)}1A` : "rgba(0,0,0,0.05)",
                                            color: ev.speed !== null ? getSpeedColor(ev.speed) : "#64748b",
                                            fontSize: 9.5,
                                            fontWeight: 800,
                                        }}
                                    >
                                        {ev.speed !== null ? `${ev.speed} km/h` : "-"}
                                    </Box>
                                </Box>
                            </ListItem>
                        ))}
                    </List>
                </Paper>,
                document.body
            )}
        </AdvancedMarkerWithRef>
        {outsidePoints.map(point => (
            <AdvancedMarker
                key={point.id}
                position={{ lat: point.lat, lng: point.lng }}
                title={`Ponto fora do corredor às ${point.time}`}
                zIndex={10}
                collisionBehavior="REQUIRED"
                onClick={() => setSelectedOutsidePointId(point.id)}
            >
                <Box sx={{ width: 16, height: 16, borderRadius: "50%", backgroundColor: "#b91c1c",
                    border: "2px solid #fff", boxShadow: "0 1px 6px rgba(127, 29, 29, 0.7)" }} />
            </AdvancedMarker>
        ))}
        {selectedOutsidePoint && (
            <InfoWindow
                position={{ lat: selectedOutsidePoint.lat, lng: selectedOutsidePoint.lng }}
                pixelOffset={[0, -18]}
                shouldFocus={false}
                onCloseClick={() => setSelectedOutsidePointId(null)}
            >
                <Box sx={{ minWidth: 210, maxWidth: 280, fontFamily: "Prometo", color: "#263b40" }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#b91c1c", mb: 0.5 }}>
                        Ponto fora do corredor
                    </Typography>
                    <Typography sx={{ fontSize: 11 }}>Horário: {selectedOutsidePoint.time}</Typography>
                    <Typography sx={{ fontSize: 11 }}>Coordenadas: {selectedOutsidePoint.lat.toFixed(6)}, {selectedOutsidePoint.lng.toFixed(6)}</Typography>
                    <Typography sx={{ fontSize: 11 }}>Velocidade: {Math.round(selectedOutsidePoint.speed)} km/h</Typography>
                    <Typography sx={{ fontSize: 11 }}>Dispositivo: {selectedOutsidePoint.deviceId}</Typography>
                    <Typography sx={{ fontSize: 11 }}>Condutor: {selectedOutsidePoint.driver}</Typography>
                    {selectedOutsidePoint.routeName && (
                        <Typography sx={{ fontSize: 11 }}>Rota: {selectedOutsidePoint.routeName}</Typography>
                    )}
                    {selectedOutsidePoint.corridorMeters != null && (
                        <Typography sx={{ fontSize: 11 }}>Corredor previsto: {selectedOutsidePoint.corridorMeters} m</Typography>
                    )}
                    <Typography sx={{ fontSize: 11 }}>Endereço: {selectedOutsidePoint.address || "Não disponível"}</Typography>
                </Box>
            </InfoWindow>
        )}
        </>
    );
};

export default GVehicleLiveTracker;
